import { AudioStream, ZoomWebcamStream } from '@dubb-app/recorder'
import * as Sentry from '@sentry/browser'
import { createGlobalState, useLocalStorage, useRafFn, watchImmediate } from '@vueuse/core'
import { computed, getCurrentInstance, ref, shallowRef } from 'vue'
import storageKeys from '../../../../common/storage-keys'
import { WEBCAM_QUALITY_AUTO } from './constants.js'

export const useWebcamRecorder = createGlobalState(() => {
  const webcamDeviceId = useLocalStorage(storageKeys.WEBCAM_RECORDER.CAMERA_DEVICE_ID, null)
  const microphoneDeviceId = useLocalStorage(storageKeys.WEBCAM_RECORDER.MICROPHONE_DEVICE_ID, null)
  const videoResolution = useLocalStorage(storageKeys.WEBCAM_RECORDER.VIDEO_RESOLUTION, WEBCAM_QUALITY_AUTO)
  const isMirrorVideo = useLocalStorage(storageKeys.WEBCAM_RECORDER.IS_MIRROR_VIDEO, false)
  const isDisplayTeleprompter = useLocalStorage(storageKeys.WEBCAM_RECORDER.IS_DISPLAY_TELEPROMPTER, true)
  const isVirtualBackgroundBlur = useLocalStorage(storageKeys.WEBCAM_RECORDER.IS_BLUR_BACKGROUND, false)
  const isVirtualBackgroundImage = useLocalStorage(storageKeys.WEBCAM_RECORDER.IS_VIRTUAL_BACKGROUND, false)
  const virtualBackgroundImage = useLocalStorage(storageKeys.WEBCAM_RECORDER.VIRTUAL_BACKGROUND_IMAGE, null)

  const webcamScale = ref(1)
  const webcamTranslate = ref({ x: 0, y: 0 })
  const canvasRef = ref()

  const webcamRecordingStream = new ZoomWebcamStream()

  // Reactive tracking of output canvas dimensions
  const webcamOutputCanvasSize = shallowRef({ width: 0, height: 0 })

  webcamRecordingStream.on('outputCanvasSizeChange', (size) => {
    webcamOutputCanvasSize.value = size
  })

  const virtualBackgroundImageUrl = computed(() => {
    if (isVirtualBackgroundBlur.value) {
      return 'blur'
    }
    else if (isVirtualBackgroundImage.value && virtualBackgroundImage.value) {
      return virtualBackgroundImage.value
    }

    return undefined
  })

  const currentInstance = getCurrentInstance()

  const handelWebcamError = (err) => {
    let message = 'Unable to access the webcam device. Please check your device settings and permissions.'
    if (err.name === 'OverconstrainedError') {
      message = 'The selected webcam device is not supported with the current video quality settings. Please try changing the video quality or selecting a different webcam device.'

      currentInstance.proxy.quality = WEBCAM_QUALITY_AUTO
    }

    console.error(err)
    Sentry.captureException(err)

    window.SwalAlert.fire({
      title: 'Error',
      text: message,
    })
  }

  watchImmediate(webcamDeviceId, (deviceId) => {
    webcamRecordingStream.setDeviceId(deviceId).catch(handelWebcamError)
  })

  watchImmediate(isMirrorVideo, (isMirror) => {
    webcamRecordingStream.setIsMirror(isMirror).catch(handelWebcamError)
  })

  watchImmediate(virtualBackgroundImageUrl, (url) => {
    webcamRecordingStream.setVirtualBackgroundImageUrl(url).catch(handelWebcamError)
  })

  watchImmediate(isVirtualBackgroundBlur, (newVal) => {
    if (newVal) {
      isVirtualBackgroundImage.value = false
    }
  })

  watchImmediate(isVirtualBackgroundImage, (newVal) => {
    if (newVal) {
      isVirtualBackgroundBlur.value = false
    }
  })

  const audioRecordingStream = new AudioStream()
  const audioVolume = ref(0)

  watchImmediate(microphoneDeviceId, (deviceId) => {
    audioRecordingStream.setDeviceId(deviceId)
  })

  useRafFn(() => {
    audioVolume.value = (audioRecordingStream.audioVolume?.getVolume() || 0) * 1.2
  })

  return {
    webcamDeviceId,
    microphoneDeviceId,
    videoResolution,
    isMirrorVideo,
    isDisplayTeleprompter,
    isVirtualBackgroundBlur,
    isVirtualBackgroundImage,
    virtualBackgroundImage,

    webcamScale,
    webcamTranslate,

    canvasRef,

    audioRecordingStream,
    audioVolume,

    webcamRecordingStream,
    webcamOutputCanvasSize,
    virtualBackgroundImageUrl,

    handelWebcamError,
  }
})
